import React, { useContext } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useRouter as useNextRouter } from 'next/router';
import Navigation, {
  NavigationBarIcon,
  NavigationLink,
  MainNavigationTab,
} from '@fuww/library/src/Navigation';
import Anchor from '@fuww/library/src/Anchor';
import { MessageDescriptor, useIntl } from 'react-intl';
import SearchIcon from '@fuww/library/src/Icons/Search';
import MailIcon from '@fuww/library/src/Icons/Mail';
import CompactButton from '@fuww/library/src/Button/CompactButton';
import { useSiteContext } from '../SiteContext';
import UserTab from './UserTab';
import { getNavigation as getTabs } from '../../data/navigation';
import LinkAndAnchor from '../LinkAndAnchor';
import messages from '../../lib/messages.json';
import { UserContext } from '../UserProvider';
import { hasRoleInAnyGroup } from '../../lib/hasUserRoles';
import { saveRouterState } from '../../lib/routerState';

const simpleTopAppBarProperties = {
  onOpenDrawer: PropTypes.func.isRequired,
  topAppBarTitle: PropTypes.node,
  useRouter: PropTypes.bool,
  isExecutive: PropTypes.bool,
};

type SimpleTopAppBarProperties = InferProps<typeof simpleTopAppBarProperties>;

const SimpleTopAppBar = ({
  onOpenDrawer,
  topAppBarTitle,
  useRouter: menuUseRouter,
  isExecutive,
}: SimpleTopAppBarProperties) => {
  const intl = useIntl();
  const router = useNextRouter();
  const {
    newsboard, locale, lite, hasNews,
  } = useSiteContext();
  const { user, roles } = useContext(UserContext);

  const tabs = getTabs(
    newsboard,
    locale,
    lite,
    hasNews,
    origin,
  ).map(({
    id,
    href: originalHref,
    router: itemUseRouter,
    route,
    params,
    executive,
  }) => {
    const hrefKey = `${id}.href`;
    const label = intl.formatMessage(messages[id]);
    const hrefTranslation = messages[hrefKey] as MessageDescriptor | undefined;
    const href = hrefTranslation
      ? intl.formatMessage(hrefTranslation) : originalHref;

    return itemUseRouter && menuUseRouter
      ? (
        <MainNavigationTab
          key={id}
          executive={executive}
        >
          <LinkAndAnchor
            route={route}
            params={params}
            passHref
            hoverUnderline
          >
            <NavigationLink
              tag="span"
              variant="button"
            >
              {label}
            </NavigationLink>
          </LinkAndAnchor>
        </MainNavigationTab>
      )
      : (
        <MainNavigationTab
          key={id}
          executive={executive}
        >
          <Anchor
            href={href}
            styled
            hoverUnderline
          >
            <NavigationLink
              tag="span"
              variant="button"
            >
              {label}
            </NavigationLink>
          </Anchor>
        </MainNavigationTab>
      );
  });

  const loginButton = user ? null : (
    <LinkAndAnchor
      route="login"
      aria-label={intl.formatMessage(messages.login)}
      onClick={() => {
        saveRouterState(router);
      }}
      fullWidth
    >
      <CompactButton
        size="small"
        variant="outlined"
        color="inherit"
      >
        <span>
          {intl.formatMessage(messages.login)}
        </span>
      </CompactButton>
    </LinkAndAnchor>
  );

  return (
    <Navigation
      onOpenDrawer={onOpenDrawer}
      topAppBarTitle={topAppBarTitle}
      tabs={tabs}
      userButtons={
        (roles && hasRoleInAnyGroup(roles, 'profiles')) ? (
          <Anchor
            href="https://dashboard.fashionunited.com"
            target="_blank"
            styled
          >
            <CompactButton size="small" variant="outlined" color="inherit">
              {intl.formatMessage(messages.dashboard)}
            </CompactButton>
          </Anchor>
        ) : loginButton
      }
      userTabs={(
        <>
          <LinkAndAnchor
            href={intl.formatMessage(messages['newsletter.href'])}
            aria-label="Newsletter"
          >
            <NavigationBarIcon
              collapsible
            >
              <MailIcon />
            </NavigationBarIcon>
          </LinkAndAnchor>
          {lite ? null : (
            <LinkAndAnchor
              route="search"
              aria-label="Search page"
              passHref
            >
              <NavigationBarIcon
                collapsible
              >
                <SearchIcon />
              </NavigationBarIcon>
            </LinkAndAnchor>
          )}
          <UserTab />
        </>
      )}
      isExecutive={isExecutive ?? false}
    />
  );
};

SimpleTopAppBar.propTypes = simpleTopAppBarProperties;

export default SimpleTopAppBar;
